import type { LucideIcon } from "lucide-react";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import { PricingFeatureList, type PricingVariant } from "@/components/landing/pricing-section/PricingFeatureList";
import { cn } from "@/lib/utils";
import {
  billingCyclePeriod,
  billingCyclePriceLabel,
  CURRENCY,
  formatMadAmount,
  inactiveBillingCycle,
  type BillingCycle,
} from "./pricingPageCopy";

type SubscriptionPrice =
  | {
      kind: "free";
      amount: number;
      period: string;
    }
  | {
      kind: "subscription";
      semester: number;
      yearly: number;
    };

type PricingPageCardProps = {
  variant: PricingVariant;
  icon: LucideIcon;
  label: string;
  tagline: string;
  features: readonly string[];
  cta: {
    label: string;
    href: string;
  };
  price: SubscriptionPrice;
  activeBillingCycle: BillingCycle;
  badge?: string;
};

const CARD_STYLES: Record<
  PricingVariant,
  {
    article: string;
    label: string;
    price: string;
    divider: string;
    button: string;
    badge: string;
  }
> = {
  purple: {
    article:
      "border-violet-200 bg-gradient-to-b from-violet-50/75 via-white to-white shadow-[0_28px_90px_-58px_rgba(109,40,217,0.45)]",
    label: "text-violet-700",
    price: "text-violet-700",
    divider: "border-violet-200",
    button:
      "bg-violet-700 shadow-[0_18px_36px_-24px_rgba(109,40,217,0.95)] hover:bg-violet-800 focus-visible:outline-violet-700",
    badge: "bg-violet-700 text-white",
  },
  orange: {
    article:
      "border-orange-200 bg-gradient-to-b from-orange-50/80 via-white to-white shadow-[0_28px_90px_-58px_rgba(234,88,12,0.45)]",
    label: "text-orange-600",
    price: "text-orange-600",
    divider: "border-orange-200",
    button:
      "bg-orange-600 shadow-[0_18px_36px_-24px_rgba(234,88,12,0.95)] hover:bg-orange-700 focus-visible:outline-orange-600",
    badge: "bg-orange-600 text-white",
  },
  green: {
    article:
      "border-green-200 bg-gradient-to-b from-green-50/80 via-white to-white shadow-[0_28px_90px_-58px_rgba(8,166,47,0.5)]",
    label: "text-[#08A62F]",
    price: "text-[#08A62F]",
    divider: "border-green-200",
    button:
      "bg-[#08A62F] shadow-[0_18px_36px_-24px_rgba(8,166,47,0.95)] hover:bg-[#078F2B] focus-visible:outline-green-700",
    badge: "bg-[#08A62F] text-white",
  },
};

function priceDetails(price: SubscriptionPrice, activeBillingCycle: BillingCycle) {
  if (price.kind === "free") {
    return {
      amount: price.amount,
      period: price.period,
      secondary: undefined,
    };
  }

  const inactiveCycle = inactiveBillingCycle(activeBillingCycle);
  const activeAmount = activeBillingCycle === "semester" ? price.semester : price.yearly;
  const inactiveAmount = inactiveCycle === "semester" ? price.semester : price.yearly;

  return {
    amount: activeAmount,
    period: billingCyclePeriod(activeBillingCycle),
    secondary: `${formatMadAmount(inactiveAmount)} ${CURRENCY} / ${billingCyclePriceLabel(inactiveCycle)}`,
  };
}

export function PricingPageCard({
  variant,
  icon: Icon,
  label,
  tagline,
  features,
  cta,
  price,
  activeBillingCycle,
  badge,
}: PricingPageCardProps) {
  const styles = CARD_STYLES[variant];
  const details = priceDetails(price, activeBillingCycle);

  return (
    <article
      className={cn(
        "relative flex h-full min-w-0 flex-col rounded-[24px] border p-6 text-center sm:p-7",
        styles.article,
      )}
    >
      {badge ? (
        <span
          className={cn(
            "absolute right-5 top-5 rounded-full px-3 py-1 text-[11px] font-black uppercase tracking-[0.12em] shadow-sm",
            styles.badge,
          )}
        >
          {badge}
        </span>
      ) : null}

      <div className={cn("flex items-center justify-center gap-3 text-sm font-black uppercase", styles.label)}>
        <Icon className="h-6 w-6" strokeWidth={2.4} aria-hidden />
        <span>{label}</span>
      </div>

      <div className="mt-6">
        <p className={cn("flex items-baseline justify-center gap-2 text-[44px] font-black leading-none", styles.price)}>
          <span>{formatMadAmount(details.amount)}</span>
          <span className="text-[22px] font-black">{CURRENCY}</span>
          <span className="text-base font-bold text-slate-600">{details.period}</span>
        </p>
        {details.secondary ? (
          <p className="mt-3 text-base font-black leading-tight text-slate-600">
            {details.secondary}
          </p>
        ) : null}
      </div>

      <p className="mx-auto mt-5 max-w-[250px] text-pretty text-[16px] font-medium leading-7 text-slate-700">
        {tagline}
      </p>

      <div className={cn("my-7 border-t", styles.divider)} aria-hidden />

      <div className="flex-1">
        <PricingFeatureList features={features} variant={variant} />
      </div>

      <Link
        href={cta.href}
        className={cn(
          "mt-7 inline-flex h-14 w-full items-center justify-center gap-3 rounded-full px-5 text-base font-black text-white transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4",
          styles.button,
        )}
      >
        {cta.label}
        <ArrowRight className="h-5 w-5" strokeWidth={2.5} aria-hidden />
      </Link>
    </article>
  );
}
